<template>
  <div class="tw-flex tw-flex-col tw-gap-5">
    <!-- Upload Area -->
    <div id="document-upload" class="tw-flex tw-flex-col tw-gap-1">
      <div>
        <label class="heading">Extract data from document</label>
        <label class="heading">Returns raw data as CSV</label>

        <!-- PDF Upload Section (availalbe to anyone with data extraction feature flag ) -->
        <div v-if="canUploadPdf" class="tw-w-full tw-flex tw-gap-3 tw-items-center">
          <o-upload v-model="files" multiple>
            <a>
              <i class="fa fa-file-pdf tw-mr-1"></i>
              Upload PDF(s) to extract raw data
            </a>
          </o-upload>
        </div>

        <!-- Manual Input Section (Restricted by OCR support feature flag) -->
        <div v-if="canManualInput && files.length === 0" class="tw-flex tw-gap-3 tw-items-center tw-mt-3">
          <input
            v-model="inputDocumentName"
            placeholder="Enter Document Name"
            class="tw-input tw-border tw-px-3"
          />
          <input
            v-model="inputPortfolioId"
            placeholder="Enter Portfolio ID"
            class="tw-input tw-border tw-px-3"
          />
          <button
            @click="startAnalysis"
            :disabled="!inputDocumentName || !inputPortfolioId"
            class="tw-btn tw-bg-blue-500"
          >
            Extract data
          </button>
        </div>

      </div>
    </div>

    <!-- Upload status -->
    <div v-if="isUploading" class="tw-flex tw-flex-col tw-gap-2 tw-text-slate-500">
      <div>
        <p>Processing...</p>
      </div>
    </div>

    <!-- Download CSV -->
    <div v-if="csvFiles.length" class="tw-flex tw-flex-col tw-gap-1">
      <p class="tw-font-semibold tw-text-lg">Download raw data:</p>
      <a v-for="file in csvFiles" v-if="file" :key="file?.filename" :href="file?.url" class="tw-text-sm">
        CSV file for {{ file?.filename }}
      </a>
    </div>
  </div>
</template>

<script>

 // Helpers and other imports
 import { getCSVFileURL } from '@frontend/shared/exceljs/helpers';
 import { postSendInternalEmail } from '@frontend/api/emailsApi.js';
 import * as Sentry from '@sentry/vue';


export default {
  props: {
    canUploadPdf: {
      type: Boolean,
      default: false,
    },
    // only true for OCR support feature flag
    canManualInput: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      files: [], // For PDF uploads
      inputDocumentName: '', // Manual input for document name
      inputPortfolioId: '', // Manual input for portfolio ID
      isUploading: false,
      csvFiles: [],
      taskIDs: {},
      statusData: {},
      taskCheckInterval: null,  // Interval for checking task status
    };
  },
  computed: {
     headers() {
       return {
         'X-CSRFToken': this.$root.$data.csrfToken,
       };
     },
     filename() {
       const name = this.file?.name;
       if (!name) return null;
        const limit = 50;
       if (name.length > limit) {
         return name.substring(0, limit) + '...';
       }
       return name;
     },
     activeStatus() {
       const statuses = Object.values(this.statusData);
       if (!statuses.length) return { };
        const sorted = statuses.sort((a, b) => a.progress - b.progress);
       return sorted[0];
     },
     failedUploads() {
       return this.failedTasks?.map(task => task.meta.statement_name);
     },
   },

watch: {
     'files': {
       handler: function(val) {
         if (val?.length) {
           this.startAnalysis();
         }
       },
       deep: true,
     },
     'activeStatus': function() {
       this.statusMessage = this.activeStatus?.msg;
     },
   },

  methods: {

    startAnalysis() {
      this.isUploading = true;

      if (this.files.length > 0) {
        this.files.forEach(file => this.sendStatement(file));
      } else if (this.canManualInput && this.inputDocumentName && this.inputPortfolioId) {
        this.queryAndProcessData();
      }

      // Ensure the task check is started after sending statement or processing data
      this.taskCheckInterval = setInterval(() => this.checkTaskStatus(), 2500);
    },

    async sendStatement(file) {
      try {
        const url = '/api/v1/process/document_upload_extraction/';
        const formData = new FormData();
        formData.append('statement', file);

        const res = await fetch(url, {
          method: 'POST',
          headers: this.headers,
          body: formData,
        });

        if (!res.ok) {
          console.error('Error during file upload:', await res.text());
          alert('Error extracting data. The YourStake team has been notified.');
          // Send email notification to your team
          await postSendInternalEmail('portfolio_checkup/error/data_extraction_error.yaml');

          return;
        }

        const data = await res.json();
        if (data.task_ids) {
          this.taskIDs[file.name] = data.task_ids['get_raw_data'];
        }
      } catch (error) {
        console.error('Error during sendStatement:', error);
        Sentry.captureException(new Error(`Raw data extraction failed: ${error.message}`));
        alert('Error extracting data. The YourStake team has been notified.');
        await postSendInternalEmail('portfolio_checkup/error/data_extraction_error.yaml');
      }
    },

    async queryAndProcessData() {
      try {
        const formData = new FormData();
        formData.append('document_name', this.inputDocumentName);
        formData.append('upload_id', this.inputPortfolioId);

        const url = '/api/v1/process/document_upload_extraction/';
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: formData,
        });

        if (!res.ok) {
          console.error('Error during manual data processing');
          alert('None found, double check input');
          return;
        }

        const data = await res.json();
        if (data.task_ids) {
          this.taskIDs[this.inputDocumentName] = data.task_ids['get_raw_data'];
        }
      } catch (error) {
        console.error('Error during queryAndProcessData:', error);
        alert('None found, double check input');
      }
    },

    async checkTaskStatus() {
      const taskIDs = Object.values(this.taskIDs).filter(taskID => !!taskID);
      if (!taskIDs.length) return;

      const url = `/api/v1/tasks/?task_ids=${taskIDs.join(',')}`;
      const res = await fetch(url, { method: 'GET', headers: this.headers });

      const tasks = (await res.json()).tasks;

      const failedTasks = [];       // Array to collect failed task names

      tasks.forEach(task => {
        if (task.status === 'complete') {
          const result = task.meta.raw_data;
          const filename = `RAW_DATA_${task.meta.statement_name}`;
          const csvdata = { content: result };
          const url = getCSVFileURL(csvdata);
          
          // Check if CSV file is already added to avoid duplicates
          if (!this.csvFiles.find(file => file.filename === filename)) {
            this.csvFiles.push({ filename, url });
          }

          // Remove the completed task from taskIDs
          delete this.taskIDs[task.meta.statement_name];
        } else if (task.status === 'failed') {
          // Handle task failure
          console.error(`Task failed for ${task.meta.statement_name}:`, task);
          failedTasks.push(task.meta.statement_name);
          // Remove the failed task from taskIDs to avoid checking it again
          delete this.taskIDs[task.meta.statement_name];
        } 
      });

      if (failedTasks.length > 0) {
        alert('Error extracting data for some documents. The YourStake team has been notified.');

        // Send email notification to your team
        await postSendInternalEmail('portfolio_checkup/error/data_extraction_error.yaml');
      }

      // Only clear the interval once all tasks are complete
      if (Object.keys(this.taskIDs).length === 0) {
        this.isUploading = false;
        clearInterval(this.taskCheckInterval);
        console.log('All tasks complete, interval cleared.');
      }
    },

  beforeDestroy() {
    clearInterval(this.taskCheckInterval);  // Ensure interval is cleared on component destroy
  },
  
}
}


</script>

<style scoped>
.tw-input {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.tw-btn {
  padding: 8px 16px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.tw-btn:disabled {
  background-color: #a0c4e8;
  cursor: not-allowed;
}
</style>